import { useApiFetch } from '~/composables/useApiFetch';
import type {
  Bind2FAPayload,
  ChangePasswordPayload,
  Disable2FAPayload,
  GoogleOAuthLoginPayload,
  GoogleOAuthLoginResponse,
  Login2FAPayload,
  LoginPayload,
  Member,
  RegisterPayload,
  ResetPasswordPayload,
  SendCodeResponse,
  Setup2FAResponse,
} from '~/types/auth';
import { unwrapEnvelope } from '~/utils/apiEnvelope';

export async function loginApi(payload: LoginPayload): Promise<Member> {
  const response = await useApiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify(payload),
    handleError: false,
  });
  return unwrapEnvelope<Member>(await response.json());
}

export async function registerApi(payload: RegisterPayload): Promise<Member> {
  const response = await useApiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload),
    handleError: false,
  });
  return unwrapEnvelope<Member>(await response.json());
}

export async function resetPasswordApi(payload: ResetPasswordPayload): Promise<Member> {
  const response = await useApiFetch('/auth/reset_password', {
    method: 'POST',
    body: JSON.stringify(payload),
    handleError: false,
  });
  return unwrapEnvelope<Member>(await response.json());
}

export async function refreshTokenApi(refreshToken: string): Promise<Member> {
  const response = await useApiFetch('/auth/refresh/token', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
    requireAuth: false, // 刷新 token 的請求本身不需要認證，避免觸發 401 處理邏輯造成無限循環
  });
  return unwrapEnvelope<Member>(await response.json());
}

export async function getCsrfTokenApi(): Promise<string> {
  const response = await useApiFetch('/csrf-token', {
    method: 'GET',
    requireAuth: true,
  });
  const data = unwrapEnvelope<{ csrf_token?: string }>(await response.json());
  return data.csrf_token ?? '';
}

export async function sendRegisterCodeApi(email: string): Promise<SendCodeResponse> {
  const response = await useApiFetch('/auth/sendcode', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return unwrapEnvelope<SendCodeResponse>(await response.json());
}

export async function validateCodeApi(code: string, email: string): Promise<void> {
  await useApiFetch(
    `/auth/validate_code?code=${code}&email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
      handleError: false,
    }
  );
}

export async function checkEmailRegisteredApi(email: string): Promise<boolean> {
  const response = await useApiFetch(
    `/auth/check_email?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
    }
  );
  return unwrapEnvelope<boolean>(await response.json());
}

export const changePasswordApi = async (data: ChangePasswordPayload): Promise<void> => {
  await useApiFetch('/profile/modifypassword', {
    method: 'PUT',
    body: JSON.stringify(data),
    requireAuth: true,
    handleError: false,
  });
};

// 2FA related API functions
// export const enable2FAApi = async (data: Enable2FAPayload): Promise<void> => {
//   await useApiFetch('/profile/enable2fa', {
//     method: 'POST',
//     body: JSON.stringify(data),
// //     requireAuth: true,
//   });
// };

export const disable2FAApi = async (data: Disable2FAPayload): Promise<void> => {
  await useApiFetch('/2fa/disable2fa', {
    method: 'POST',
    body: JSON.stringify(data),
    requireAuth: true,
    handleError: false,
  });
};

export const setup2FAApi = async (): Promise<Setup2FAResponse> => {
  const response = await useApiFetch('/2fa/setup', {
    method: 'GET',
    requireAuth: true,
    handleError: false,
  });
  return unwrapEnvelope<Setup2FAResponse>(await response.json());
};

export const bind2FAApi = async (data: Bind2FAPayload): Promise<void> => {
  await useApiFetch('/2fa/bind', {
    method: 'POST',
    body: JSON.stringify(data),
    requireAuth: true,
    handleError: false,
  });
};

export const login2FAApi = async (data: Login2FAPayload): Promise<Member> => {
  const response = await useApiFetch('/auth/login2fa', {
    method: 'POST',
    body: JSON.stringify(data),
    handleError: false,
  });
  return unwrapEnvelope<Member>(await response.json());
};

export const googleOAuthLoginApi = async (
  data: GoogleOAuthLoginPayload
): Promise<GoogleOAuthLoginResponse> => {
  const response = await useApiFetch('/auth/google/oauth2', {
    method: 'POST',
    body: JSON.stringify(data),
    handleError: false,
  });
  return unwrapEnvelope<GoogleOAuthLoginResponse>(await response.json());
};

/**
 * 登出 API
 * @param deviceId 可選的設備 ID，如果提供則只登出該設備，否則登出所有設備
 */
export const logoutApi = async (deviceId?: string): Promise<void> => {
  const url = deviceId ? `/auth/logout?device_id=${encodeURIComponent(deviceId)}` : '/auth/logout';
  await useApiFetch(url, {
    method: 'POST',
    requireAuth: true,
    handleError: false, // 登出時即使 API 失敗也要清除本地資料
  });
};
